import { useI18n, useT, type Translate } from "../../../shared/i18n/i18n";
import { useEffect, useMemo, useState } from "react";
import {
  Bot,
  Check,
  Copy,
  Crown,
  Loader2,
  Mail,
  MailPlus,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Shield,
  Trash2,
  UserPlus,
  UserRound,
  Users,
  UsersRound,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useWorkspaceTeam } from "../../../entities/access/model/use-workspace-access";

type TeamData = NonNullable<ReturnType<typeof useWorkspaceTeam>["data"]>;
type Member = TeamData["members"][number];
type WorkspaceRole = Member["role"];
type InviteRole = "admin" | "member" | "guest";

// Built per render rather than as a module constant: the labels and hints are
// screen text, and screen text follows the reader's language.
function roleMeta(t: Translate): Record<WorkspaceRole, { badge: "default" | "secondary" | "outline"; hint: string; label: string }> {
  return {
    owner: { badge: "default", hint: t("team.ownerHint"), label: t("team.owner") },
    admin: { badge: "secondary", hint: t("team.adminHint"), label: t("team.admin") },
    member: { badge: "outline", hint: t("team.memberHint"), label: t("team.member") },
    guest: { badge: "outline", hint: t("team.guestHint"), label: t("team.guest") },
  };
}

const AVATAR_PALETTE = [
  "bg-rose-500/15 text-rose-600 dark:text-rose-300",
  "bg-orange-500/15 text-orange-600 dark:text-orange-300",
  "bg-amber-500/15 text-amber-600 dark:text-amber-300",
  "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  "bg-teal-500/15 text-teal-600 dark:text-teal-300",
  "bg-sky-500/15 text-sky-600 dark:text-sky-300",
  "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300",
  "bg-violet-500/15 text-violet-600 dark:text-violet-300",
  "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300",
];

function hashString(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

function initials(label: string) {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  const first = parts.at(0);
  if (!first) return "?";
  const last = parts.at(-1);
  if (parts.length === 1 || !last) return first.slice(0, 2).toUpperCase();
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function PersonAvatar({ className, name, seed }: { className?: string; name: string; seed: string }) {
  const palette = AVATAR_PALETTE[hashString(seed) % AVATAR_PALETTE.length];
  return (
    <span className={cn("grid size-9 shrink-0 place-items-center rounded-full text-xs font-semibold uppercase", palette, className)}>
      {initials(name)}
    </span>
  );
}

function memberName(member: Member) {
  return member.displayName ?? member.email;
}

function daysUntil(date: Date) {
  return Math.ceil((new Date(date).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

export function WorkspaceTeamPanel() {
  const t = useT();
  const { locale } = useI18n();
  const team = useWorkspaceTeam();
  const [actionError, setActionError] = useState<string | null>(null);

  const allMembers = team.data?.members ?? [];
  // People and service accounts share one members table, but they are not the
  // same thing to look at: one gets invited and can sign in, the other exists
  // so an integration has access of its own.
  const members = allMembers.filter((member) => !member.isServiceAccount);
  const serviceAccounts = allMembers.filter((member) => member.isServiceAccount);
  const teams = team.data?.teams ?? [];
  const invitations = team.data?.invitations ?? [];
  const currentRole = members.find((member) => member.userId === team.currentUserId)?.role ?? null;
  const canManageAdmins = currentRole === "owner";
  const ownerCount = members.filter((member) => member.role === "owner").length;

  function memberGuard(member: Member): string | null {
    if (member.role === "owner" && ownerCount <= 1) return "Workspace must keep at least one owner.";
    if ((member.role === "owner" || member.role === "admin") && !canManageAdmins) {
      return "Only an owner can manage owners and admins.";
    }
    return null;
  }

  if (team.isLoading && !team.data) {
    return (
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6">
        <Skeleton className="h-14 w-full" />
        <div className="grid grid-cols-3 gap-3">
          <Skeleton className="h-20" /><Skeleton className="h-20" /><Skeleton className="h-20" />
        </div>
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{t("team.title")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("team.whatItIs")}
            </p>
          </div>
          <InviteDialog canManageAdmins={canManageAdmins} onError={setActionError} onInvite={team.invite} />
        </header>

        {(team.error || actionError) ? (
          <Alert variant="destructive"><AlertDescription>{actionError ?? team.error}</AlertDescription></Alert>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatTile icon={Users} label={t("team.members")} value={members.length} />
          <StatTile icon={UsersRound} label={t("team.teams")} value={teams.length} />
          <StatTile icon={Mail} label={t("team.pending")} value={invitations.length} />
        </div>

        <Tabs defaultValue="members">
          <TabsList variant="line">
            <TabsTrigger value="members"><Users data-icon="inline-start" /> {t("team.members")} <TabCount value={members.length} /></TabsTrigger>
            <TabsTrigger value="teams"><UsersRound data-icon="inline-start" /> {t("team.teams")} <TabCount value={teams.length} /></TabsTrigger>
            <TabsTrigger value="invitations"><Mail data-icon="inline-start" /> {t("team.invitations")} <TabCount value={invitations.length} /></TabsTrigger>
            <TabsTrigger value="service"><Bot data-icon="inline-start" /> {t("team.serviceAccounts")} <TabCount value={serviceAccounts.length} /></TabsTrigger>
          </TabsList>

          <TabsContent value="members">
            <MembersTab
              currentUserId={team.currentUserId}
              memberGuard={memberGuard}
              members={members}
              onError={setActionError}
              onRemove={team.removeMember}
              onRoleChange={team.updateMemberRole}
            />
          </TabsContent>

          <TabsContent value="teams">
            <TeamsTab
              members={members}
              onArchive={team.archiveTeam}
              onCreate={team.createTeam}
              onError={setActionError}
              onUpdate={team.updateTeam}
              teams={teams}
            />
          </TabsContent>

          <TabsContent value="invitations">
            <InvitationsTab invitations={invitations} onError={setActionError} onRevoke={team.revokeInvitation} />
          </TabsContent>

          <TabsContent value="service">
            <ServiceAccountsTab
              accounts={serviceAccounts}
              onCreate={team.createServiceAccount}
              onError={setActionError}
              onRemove={team.removeMember}
              onRoleChange={team.updateMemberRole}
            />
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
}

// An identity for a robot. It is listed apart from people because everything
// about it differs: it is created rather than invited, it cannot sign in, and
// the only interesting question about it is what it is allowed to reach.
function ServiceAccountsTab({ accounts, onCreate, onError, onRemove, onRoleChange }: {
  accounts: Member[];
  onCreate: (input: { name: string; role: "guest" | "member" }) => Promise<unknown>;
  onError: (message: string | null) => void;
  onRemove: (userId: string) => Promise<void>;
  onRoleChange: (userId: string, role: "owner" | "admin" | "member" | "guest") => Promise<void>;
}) {
  const t = useT();
  const [name, setName] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  async function create() {
    const trimmed = name.trim();
    if (trimmed.length < 2) return;
    setIsCreating(true);
    onError(null);
    try {
      await onCreate({ name: trimmed, role: "member" });
      setName("");
    } catch (err) {
      onError(err instanceof Error ? err.message : t("team.serviceAccountFailed"));
    } finally {
      setIsCreating(false);
    }
  }

  async function run(userId: string, action: () => Promise<unknown>) {
    setBusyId(userId);
    onError(null);
    try {
      await action();
    } catch (err) {
      onError(err instanceof Error ? err.message : t("team.serviceAccountFailed"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="rounded-xl border bg-card p-4">
        <p className="text-sm">{t("team.serviceAccountsWhat")}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t("team.serviceAccountsWhy")}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Input
            className="h-9 min-w-0 flex-1 sm:max-w-xs"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void create();
            }}
            placeholder={t("team.serviceAccountPlaceholder")}
            value={name}
          />
          <Button disabled={isCreating || name.trim().length < 2} onClick={() => void create()}>
            {isCreating ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <Plus data-icon="inline-start" />}
            {t("team.serviceAccountCreate")}
          </Button>
        </div>
      </div>

      {accounts.length === 0 ? null : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <ul className="divide-y">
            {accounts.map((account) => (
              <li className="flex items-center gap-3 p-3 sm:p-4" key={account.userId}>
                <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
                  <Bot className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{memberName(account)}</p>
                  <p className="truncate text-xs text-muted-foreground">{t("team.serviceAccountCannotSignIn")}</p>
                </div>
                {busyId === account.userId ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
                <RoleSelect
                  disabled={busyId === account.userId}
                  onChange={(role) => void run(account.userId, () => onRoleChange(account.userId, role))}
                  value={account.role}
                />
                <Button
                  aria-label={t("team.serviceAccountRemove", { name: memberName(account) })}
                  disabled={busyId === account.userId}
                  onClick={() => void run(account.userId, () => onRemove(account.userId))}
                  size="icon-sm"
                  variant="ghost"
                >
                  <Trash2 />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: number }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card p-4">
      <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="size-5" /></span>
      <div>
        <p className="text-2xl font-semibold leading-none tabular-nums">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function TabCount({ value }: { value: number }) {
  return <span className="ml-1 rounded-full bg-muted px-1.5 text-[0.625rem] font-medium tabular-nums text-muted-foreground">{value}</span>;
}

function RoleSelect({ disabled, onChange, value }: { disabled?: boolean; onChange: (role: WorkspaceRole) => void; value: WorkspaceRole }) {
  const t = useT();
  const roles = roleMeta(t);
  return (
    <Select disabled={disabled} onValueChange={(next) => onChange(next as WorkspaceRole)} value={value}>
      <SelectTrigger className="w-32" size="sm"><SelectValue>{(value: WorkspaceRole) => roles[value].label}</SelectValue></SelectTrigger>
      <SelectContent className="w-72">
        {(["owner", "admin", "member", "guest"] as const).map((role) => (
          <SelectItem className="py-1.5 pr-8" key={role} value={role}>
            <span className="flex flex-col gap-0.5">
              <span className="text-sm leading-none">{roles[role].label}</span>
              <span className="whitespace-normal text-xs leading-snug text-muted-foreground">{roles[role].hint}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MembersTab({ currentUserId, memberGuard, members, onError, onRemove, onRoleChange }: {
  currentUserId: string | null;
  memberGuard: (member: Member) => string | null;
  members: Member[];
  onError: (message: string | null) => void;
  onRemove: (userId: string) => Promise<void>;
  onRoleChange: (userId: string, role: WorkspaceRole) => Promise<void>;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return members;
    return members.filter((member) => `${memberName(member)} ${member.email}`.toLowerCase().includes(normalized));
  }, [members, query]);

  async function run(userId: string, action: () => Promise<void>) {
    setBusyId(userId);
    onError(null);
    try {
      await action();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not update member.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mt-4 flex flex-col gap-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input aria-label={t("team.searchMembers")} className="pl-9" onChange={(event) => setQuery(event.target.value)} placeholder={t("team.searchMembersPlaceholder")} value={query} />
      </div>

      <div className="overflow-hidden rounded-xl border">
        {filtered.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">{t("team.noMembersMatch", { query })}</p>
        ) : (
          <ul className="divide-y">
            {filtered.map((member) => {
              const guard = memberGuard(member);
              const isSelf = member.userId === currentUserId;
              return (
                <li className="flex items-center gap-3 p-3 sm:p-4" key={member.userId}>
                  <PersonAvatar name={memberName(member)} seed={member.email} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium">{memberName(member)}</p>
                      {member.role === "owner" ? <Crown className="size-3.5 text-amber-500" /> : null}
                      {isSelf ? <Badge variant="outline">{t("team.you")}</Badge> : null}
                      {member.activatedAt ? null : <Badge variant="secondary">{t("team.notSignedIn")}</Badge>}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                  </div>
                  <span className="hidden text-xs text-muted-foreground sm:block">
                    {t(member.activatedAt ? "team.joined" : "team.invitedOn", {
                      date: new Date(member.joinedAt).toLocaleDateString(locale),
                    })}
                  </span>
                  {busyId === member.userId ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
                  <MaybeTooltip content={guard}>
                    <RoleSelect
                      disabled={busyId === member.userId || guard !== null}
                      onChange={(role) => void run(member.userId, () => onRoleChange(member.userId, role))}
                      value={member.role}
                    />
                  </MaybeTooltip>
                  <MaybeTooltip content={guard}>
                    <Button
                      aria-label={t("a11y.remove", { name: member.email })}
                      disabled={busyId === member.userId || guard !== null}
                      onClick={() => void run(member.userId, () => onRemove(member.userId))}
                      size="icon-sm"
                      variant="ghost"
                    >
                      <Trash2 />
                    </Button>
                  </MaybeTooltip>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function MaybeTooltip({ children, content }: { children: React.ReactNode; content: string | null }) {
  if (!content) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex" />}>{children}</TooltipTrigger>
      <TooltipContent>{content}</TooltipContent>
    </Tooltip>
  );
}

type TeamDialogState = { mode: "create" } | { mode: "edit"; teamId: string } | null;

function TeamsTab({ members, onArchive, onCreate, onError, onUpdate, teams }: {
  members: Member[];
  onArchive: (teamId: string) => Promise<void>;
  onCreate: (input: { memberIds: string[]; name: string }) => Promise<void>;
  onError: (message: string | null) => void;
  onUpdate: (input: { memberIds: string[]; name: string; teamId: string }) => Promise<void>;
  teams: TeamData["teams"];
}) {
  const t = useT();
  const [dialog, setDialog] = useState<TeamDialogState>(null);
  const editingTeam = dialog?.mode === "edit" ? teams.find((team) => team.id === dialog.teamId) ?? null : null;

  return (
    <div className="mt-4 flex flex-col gap-4">
      {teams.length > 0 ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">{t("team.teamsWhat")}</p>
          <Button onClick={() => setDialog({ mode: "create" })} size="sm"><Plus /> {t("team.newTeam")}</Button>
        </div>
      ) : null}

      {teams.length === 0 ? (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon"><UsersRound /></EmptyMedia>
            <EmptyTitle>{t("team.noTeams")}</EmptyTitle>
            <EmptyDescription>{t("team.teamsWhatLong")}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={() => setDialog({ mode: "create" })}><Plus /> {t("team.createFirstTeam")}</Button>
          </EmptyContent>
        </Empty>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {teams.map((team) => (
            <TeamCard
              key={team.id}
              members={members}
              onDelete={() => onArchive(team.id)}
              onError={onError}
              onManage={() => setDialog({ mode: "edit", teamId: team.id })}
              team={team}
            />
          ))}
        </div>
      )}

      <TeamDialog
        key={dialog?.mode === "edit" ? dialog.teamId : dialog?.mode ?? "closed"}
        members={members}
        onCreate={onCreate}
        onError={onError}
        onOpenChange={(open) => { if (!open) setDialog(null); }}
        onUpdate={onUpdate}
        open={dialog !== null}
        team={editingTeam}
      />
    </div>
  );
}

function TeamCard({ members, onDelete, onError, onManage, team }: {
  members: Member[];
  onDelete: () => Promise<void>;
  onError: (message: string | null) => void;
  onManage: () => void;
  team: TeamData["teams"][number];
}) {
  const t = useT();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const teamMembers = members.filter((member) => team.memberIds.includes(member.userId));

  async function remove() {
    setIsDeleting(true);
    onError(null);
    try {
      await onDelete();
      setConfirmOpen(false);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not delete team.");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <AlertDialog onOpenChange={setConfirmOpen} open={confirmOpen}>
      <div className="group flex flex-col gap-3 rounded-xl border bg-card p-4 transition-colors hover:border-border">
        <div className="flex items-start gap-3">
          <button className="flex min-w-0 flex-1 items-start gap-3 text-left" onClick={onManage} type="button">
            <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><UsersRound className="size-5" /></span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{team.name}</span>
              <span className="block text-xs text-muted-foreground">{team.memberIds.length} {team.memberIds.length === 1 ? "member" : "members"}</span>
            </span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger render={<Button aria-label={t("common.optionsFor", { name: team.name })} size="icon-sm" variant="ghost" />}><MoreHorizontal /></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onClick={onManage}><Pencil /> {t("team.manage")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setConfirmOpen(true)} variant="destructive"><Trash2 /> {t("common.delete")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {teamMembers.length > 0 ? (
          <button className="flex items-center" onClick={onManage} type="button">
            <div className="flex -space-x-2">
              {teamMembers.slice(0, 6).map((member) => (
                <PersonAvatar className="size-7 border-2 border-card text-[0.625rem]" key={member.userId} name={memberName(member)} seed={member.email} />
              ))}
            </div>
            {teamMembers.length > 6 ? <span className="ml-2 text-xs text-muted-foreground">+{teamMembers.length - 6}</span> : null}
          </button>
        ) : (
          <p className="text-xs text-muted-foreground">{t("team.noMembers")}</p>
        )}
      </div>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete “{team.name}”?</AlertDialogTitle>
          <AlertDialogDescription>{t("team.teamDelete")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction disabled={isDeleting} onClick={(event) => { event.preventDefault(); void remove(); }} variant="destructive">
            {isDeleting ? <Loader2 className="animate-spin" /> : <Trash2 />} Delete team
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function TeamDialog({ members, onCreate, onError, onOpenChange, onUpdate, open, team }: {
  members: Member[];
  onCreate: (input: { memberIds: string[]; name: string }) => Promise<void>;
  onError: (message: string | null) => void;
  onOpenChange: (open: boolean) => void;
  onUpdate: (input: { memberIds: string[]; name: string; teamId: string }) => Promise<void>;
  open: boolean;
  team: TeamData["teams"][number] | null;
}) {
  const t = useT();
  const isEdit = team !== null;
  const [name, setName] = useState(team?.name ?? "");
  const [memberIds, setMemberIds] = useState<string[]>(team?.memberIds ?? []);
  const [query, setQuery] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return members;
    return members.filter((member) => `${memberName(member)} ${member.email}`.toLowerCase().includes(normalized));
  }, [members, query]);

  async function save() {
    setIsSaving(true);
    onError(null);
    try {
      if (team) await onUpdate({ memberIds, name, teamId: team.id });
      else await onCreate({ memberIds, name });
      onOpenChange(false);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not save team.");
    } finally {
      setIsSaving(false);
    }
  }

  function toggleMember(userId: string, checked: boolean) {
    setMemberIds((current) => checked ? [...new Set([...current, userId])] : current.filter((id) => id !== userId));
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="flex max-h-[min(90vh,44rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-b p-5">
          <div className="flex items-center gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><UsersRound className="size-4" /></span>
            <div className="min-w-0">
              <DialogTitle className="text-base">{isEdit ? "Manage team" : t("team.newTeam")}</DialogTitle>
              <DialogDescription>{isEdit ? "Rename the team and choose who belongs to it." : t("team.createTeamHint")}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <label className="text-xs font-medium" htmlFor="team-dialog-name">{t("team.teamName")}</label>
          <Input
            autoFocus={!isEdit}
            className="mt-1"
            id="team-dialog-name"
            onChange={(event) => setName(event.target.value)}
            placeholder={t("team.teamNamePlaceholder")}
            value={name}
          />

          <div className="mt-4 mb-2 flex items-center justify-between">
            <p className="text-xs font-medium">{t("team.members")}</p>
            <span className="text-xs text-muted-foreground">{memberIds.length} of {members.length} selected</span>
          </div>
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input aria-label={t("team.searchToAdd")} className="pl-9" onChange={(event) => setQuery(event.target.value)} placeholder={t("team.searchMembersPlaceholder")} value={query} />
          </div>
          <div className="max-h-64 divide-y overflow-y-auto rounded-lg border">
            {filtered.length === 0 ? (
              <p className="p-6 text-center text-xs text-muted-foreground">No members match “{query}”.</p>
            ) : filtered.map((member) => {
              const checked = memberIds.includes(member.userId);
              return (
                <label className="flex cursor-pointer items-center gap-3 p-2.5 hover:bg-accent" key={member.userId}>
                  <Checkbox checked={checked} onCheckedChange={(value) => toggleMember(member.userId, value === true)} />
                  <PersonAvatar className="size-7 text-[0.625rem]" name={memberName(member)} seed={member.email} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{memberName(member)}</span>
                    <span className="block truncate text-xs text-muted-foreground">{member.email}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-end gap-2 border-t p-4">
          <Button onClick={() => onOpenChange(false)} variant="outline">{t("common.cancel")}</Button>
          <Button disabled={isSaving || name.trim().length < 2} onClick={() => void save()}>
            {isSaving ? <Loader2 className="animate-spin" /> : <Check />} {isEdit ? t("team.saveTeam") : t("team.createTeam")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvitationsTab({ invitations, onError, onRevoke }: {
  invitations: TeamData["invitations"];
  onError: (message: string | null) => void;
  onRevoke: (invitationId: string) => Promise<void>;
}) {
  const t = useT();
  const roles = roleMeta(t);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function revoke(invitationId: string) {
    setBusyId(invitationId);
    onError(null);
    try {
      await onRevoke(invitationId);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not revoke invitation.");
    } finally {
      setBusyId(null);
    }
  }

  if (invitations.length === 0) {
    return (
      <Empty className="mt-4 border">
        <EmptyHeader>
          <EmptyMedia variant="icon"><Mail /></EmptyMedia>
          <EmptyTitle>{t("team.noPending")}</EmptyTitle>
          <EmptyDescription>{t("team.inviteEmpty")}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="mt-4 overflow-hidden rounded-xl border">
      <ul className="divide-y">
        {invitations.map((invitation) => {
          const days = daysUntil(invitation.expiresAt);
          const expired = days <= 0;
          return (
            <li className="flex items-center gap-3 p-3 sm:p-4" key={invitation.id}>
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground"><Mail className="size-4" /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-medium">{invitation.email}</p>
                  <Badge variant={roles[invitation.role].badge}>{roles[invitation.role].label}</Badge>
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  Invited by {invitation.invitedByEmail}
                </p>
              </div>
              <Badge variant={expired ? "destructive" : "outline"}>
                {expired ? "Expired" : days > 3650 ? t("team.awaitingSignIn") : `Expires in ${days}d`}
              </Badge>
              <Button disabled={busyId === invitation.id} onClick={() => void revoke(invitation.id)} size="sm" variant="outline">
                {busyId === invitation.id ? <Loader2 className="animate-spin" /> : null} Revoke
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function InviteDialog({ canManageAdmins, onError, onInvite }: {
  canManageAdmins: boolean;
  onError: (message: string | null) => void;
  onInvite: (input: { displayName?: string; email: string; role: InviteRole }) => Promise<{ emailError?: string | null; invitationUrl: string }>;
}) {
  const t = useT();
  const roles = roleMeta(t);
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteRole>("member");
  const [isInviting, setIsInviting] = useState(false);
  const [invitationUrl, setInvitationUrl] = useState<string | null>(null);
  // Приглашение создано, а письмо не ушло: это предупреждение, не ошибка.
  const [emailError, setEmailError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setDisplayName("");
      setEmail("");
      setRole("member");
      setInvitationUrl(null);
      setEmailError(null);
      setCopied(false);
    }
  }, [open]);

  useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [copied]);

  const roleOptions: InviteRole[] = canManageAdmins ? ["admin", "member", "guest"] : ["member", "guest"];

  async function invite() {
    setIsInviting(true);
    onError(null);
    try {
      const result = await onInvite({ displayName: displayName.trim() || undefined, email: email.trim(), role });
      setInvitationUrl(result.invitationUrl);
      setEmailError(result.emailError ?? null);
      setDisplayName("");
      setEmail("");
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not create invitation.");
    } finally {
      setIsInviting(false);
    }
  }

  async function copyLink() {
    if (!invitationUrl) return;
    await navigator.clipboard.writeText(invitationUrl);
    setCopied(true);
  }

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger render={<Button />}><UserPlus /> {t("team.invite")}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><MailPlus className="size-4" /></span>
            <div>
              <DialogTitle className="text-base">{t("team.invite")}</DialogTitle>
              <DialogDescription>{t("team.inviteDescription")}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" htmlFor="invite-email">{t("team.email")}</label>
            <Input
              autoFocus
              id="invite-email"
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter" && email.trim()) void invite(); }}
              placeholder={t("team.emailPlaceholder")}
              type="email"
              value={email}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" htmlFor="invite-name">{t("team.inviteName")}</label>
            <Input
              id="invite-name"
              onChange={(event) => setDisplayName(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter" && email.trim()) void invite(); }}
              placeholder={t("team.inviteNamePlaceholder")}
              value={displayName}
            />
            <p className="text-xs text-muted-foreground">{t("team.inviteNameHint")}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium">{t("team.role")}</span>
            <Select onValueChange={(value) => setRole(value as InviteRole)} value={role}>
              <SelectTrigger className="w-full"><SelectValue>{(value: InviteRole) => roles[value].label}</SelectValue></SelectTrigger>
              <SelectContent className="w-[min(20rem,var(--anchor-width))]">
                {roleOptions.map((option) => (
                  <SelectItem className="py-1.5 pr-8" key={option} value={option}>
                    <span className="flex items-center gap-2">
                      {option === "admin" ? <Shield className="size-3.5 shrink-0" /> : <UserRound className="size-3.5 shrink-0" />}
                      <span className="flex flex-col gap-0.5">
                        <span className="text-sm leading-none">{roles[option].label}</span>
                        <span className="whitespace-normal text-xs leading-snug text-muted-foreground">{roles[option].hint}</span>
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {invitationUrl ? (
            <div className="flex flex-col gap-1.5 rounded-lg border bg-muted/40 p-3">
              <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">{t("team.invitationCreated")}</p>
              {emailError ? (
                <Alert>
                  <AlertDescription>{t("team.invitationEmailFailed", { error: emailError })}</AlertDescription>
                </Alert>
              ) : null}
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate rounded-md bg-background px-2 py-1.5 text-xs">{invitationUrl}</span>
                <Button aria-label={t("team.copyInvite")} onClick={() => void copyLink()} size="icon-sm" variant="outline">
                  {copied ? <Check className="text-emerald-500" /> : <Copy />}
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button onClick={() => setOpen(false)} variant="outline">{invitationUrl ? t("common.done") : t("common.cancel")}</Button>
          <Button disabled={isInviting || !email.trim()} onClick={() => void invite()}>
            {isInviting ? <Loader2 className="animate-spin" /> : <MailPlus />} {t("team.sendInvite")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
